export function parseQuarter(key) {
    const m = /^(\d{4})-Q([1-4])$/.exec(key);
    if (!m)
        throw new Error(`Invalid quarter key: ${key}`);
    return { year: Number(m[1]), q: Number(m[2]) };
}
export function quarterKey(q) {
    return `${q.year}-Q${q.q}`;
}
export function isQuarterKey(key) {
    return /^(\d{4})-Q([1-4])$/.test(key);
}
/** ISO date string helpers (YYYY-MM-DD, calendar dates without time zone). */
export function isoDate(y, m, d) {
    return `${String(y).padStart(4, '0')}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}
function daysInMonth(y, m) {
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
export function quarterRange(key) {
    const { year, q } = parseQuarter(key);
    const startMonth = (q - 1) * 3 + 1;
    const endMonth = startMonth + 2;
    return { start: isoDate(year, startMonth, 1), end: isoDate(year, endMonth, daysInMonth(year, endMonth)) };
}
export function quarterOfDate(date) {
    const y = Number(date.slice(0, 4));
    const m = Number(date.slice(5, 7));
    return quarterKey({ year: y, q: (Math.floor((m - 1) / 3) + 1) });
}
export function addQuarters(key, n) {
    const { year, q } = parseQuarter(key);
    const idx = year * 4 + (q - 1) + n;
    return quarterKey({ year: Math.floor(idx / 4), q: ((idx % 4) + 1) });
}
export function compareQuarter(a, b) {
    const qa = parseQuarter(a);
    const qb = parseQuarter(b);
    return qa.year * 4 + qa.q - (qb.year * 4 + qb.q);
}
function toUTC(date) {
    return new Date(`${date}T00:00:00Z`);
}
function fromUTC(d) {
    return d.toISOString().slice(0, 10);
}
export function addDays(date, days) {
    const d = toUTC(date);
    d.setUTCDate(d.getUTCDate() + days);
    return fromUTC(d);
}
export function diffDays(from, to) {
    return Math.round((toUTC(to).getTime() - toUTC(from).getTime()) / 86_400_000);
}
export function weekday(date) {
    return toUTC(date).getUTCDay(); // 0 = Sunday
}
/** Easter Sunday (Gregorian, anonymous algorithm). */
export function easterSunday(year) {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return isoDate(year, month, day);
}
/** General holidays per Algemene termijnenwet (art. 3). */
export function dutchGeneralHolidays(year) {
    const easter = easterSunday(year);
    const kingsDay = isoDate(year, 4, 27);
    return [
        isoDate(year, 1, 1),
        addDays(easter, -2), // Goede Vrijdag
        easter,
        addDays(easter, 1), // Tweede Paasdag
        weekday(kingsDay) === 0 ? isoDate(year, 4, 26) : kingsDay,
        isoDate(year, 5, 5), // Bevrijdingsdag
        addDays(easter, 39), // Hemelvaartsdag
        addDays(easter, 49), // Eerste Pinksterdag
        addDays(easter, 50), // Tweede Pinksterdag
        isoDate(year, 12, 25),
        isoDate(year, 12, 26),
    ];
}
export function isNonWorkingDay(date) {
    const wd = weekday(date);
    if (wd === 0 || wd === 6)
        return true;
    return dutchGeneralHolidays(Number(date.slice(0, 4))).includes(date);
}
/** The working day `n` working days before `date` (n = 0: `date` itself if it is a working day, else the one before). */
export function workingDaysBefore(date, n) {
    let d = date;
    while (isNonWorkingDay(d))
        d = addDays(d, -1);
    for (let i = 0; i < n; i++) {
        d = addDays(d, -1);
        while (isNonWorkingDay(d))
            d = addDays(d, -1);
    }
    return d;
}
/** Payment reminders go out this many working days before the deadline (bank processing time, TS-01). */
export const PAYMENT_REMINDER_WORKING_DAYS = 5;
export function filingDeadline(key, opts = {}) {
    const { year, q } = parseQuarter(key);
    const month = q * 3 + 1; // month after quarter end
    const y = month > 12 ? year + 1 : year;
    const m = month > 12 ? month - 12 : month;
    const nominal = isoDate(y, m, daysInMonth(y, m));
    let effective = nominal;
    if (opts.applyShift ?? false) {
        while (isNonWorkingDay(effective))
            effective = addDays(effective, 1);
    }
    return {
        nominal,
        effective,
        shifted: effective !== nominal,
        nonWorkingDay: isNonWorkingDay(nominal),
        lastWorkingDay: workingDaysBefore(nominal, 0),
        paymentReminderOn: workingDaysBefore(nominal, PAYMENT_REMINDER_WORKING_DAYS),
    };
}
/**
 * The quarter a user should be working on today: the most recent quarter whose deadline has not
 * passed by more than `graceDays`, i.e. "the next quarter to file", not the calendar quarter
 * (UI spec §4.3). On 2026-10-14 this returns 2026-Q3.
 */
export function defaultFilingQuarter(today) {
    const current = quarterOfDate(today);
    const previous = addQuarters(current, -1);
    return filingDeadline(previous).effective >= today ? previous : current;
}
export function quarterHasEnded(key, today) {
    return quarterRange(key).end < today;
}
/** Days from `today` until the deadline (negative when overdue). */
export function daysUntilDeadline(key, today) {
    return diffDays(today, filingDeadline(key).effective);
}
/** UI spec §5.4: >14 days neutral, 4–14 warning, ≤3 and overdue danger. */
export function deadlineTone(daysLeft) {
    if (daysLeft <= 3)
        return 'danger';
    if (daysLeft <= 14)
        return 'warning';
    return 'neutral';
}
/** Reminder offsets before the deadline (FR-23). */
export const DEADLINE_REMINDER_DAYS = [14, 7, 3, 1];
/**
 * Corrections (FR-22): a difference of at most €1,000 (too much or too little) may be processed in the next return
 * (simplified correction for periods from 2022 on); a larger one requires a suppletie "zo snel mogelijk", at the
 * latest within 8 weeks after discovering the error (Belastingdienst, "Btw-aangifte corrigeren").
 */
export function suppletieDeadline(discoveredOn) {
    return addDays(discoveredOn, 56);
}
//# sourceMappingURL=period.js.map